import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { rupiah, monthLabel, dateLabel, PAYMENT_STATUS, ATTEND_STATUS, LEVEL_META, waLink, ageLabel } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Loader2, Phone, MapPin, Calendar, Star, MessageCircle, User, Link2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/PhotoUpload";

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
      ))}
    </div>
  );
}

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [journals, setJournals] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get(`/students/${id}`).then((r) => setStudent(r.data));
    api.get("/journals", { params: { student_id: id } }).then((r) => setJournals(r.data));
    api.get("/attendance", { params: { student_id: id } }).then((r) => setAttendance(r.data));
    api.get("/payments", { params: { student_id: id } }).then((r) => setPayments(r.data));
  }, [id]);

  if (!student) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  const info = [
    { icon: User, label: "Jenis Kelamin", value: student.gender === "P" ? "Perempuan" : "Laki-laki" },
    { icon: Calendar, label: "Tempat, Tgl Lahir", value: `${student.birth_place || "-"}, ${student.birth_date ? dateLabel(student.birth_date) : "-"}${ageLabel(student.birth_date) ? ` (${ageLabel(student.birth_date)})` : ""}` },
    { icon: MapPin, label: "Alamat", value: student.address || "-" },
    { icon: Phone, label: "No. WhatsApp Wali", value: student.wa || "-" },
  ];

  return (
    <div className="space-y-6 animate-float-in">
      <Button variant="ghost" onClick={() => navigate("/siswa")} className="rounded-xl -ml-2 text-slate-600"><ArrowLeft className="w-4 h-4 mr-1" /> Kembali</Button>

      <Card className="p-6 rounded-2xl border-amber-100">
        <div className="flex flex-wrap items-start gap-5">
          <Avatar src={student.photo} name={student.name} size="w-20 h-20" rounded="rounded-3xl" text="text-3xl" />
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-3xl font-bold text-slate-900">{student.name}</h1>
              <Badge variant="outline" className={`rounded-full ${LEVEL_META[student.level]?.cls}`}>{student.level}</Badge>
              {student.class_label && <Badge variant="outline" className="rounded-full">{student.class_label}</Badge>}
              <Badge data-testid="detail-status-badge" className={`rounded-full border-0 ${student.status === "aktif" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{student.status === "aktif" ? "Aktif" : "Nonaktif"}</Badge>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {student.status !== "aktif" ? <span className="text-xs text-slate-400 italic">Jadwal tidak aktif (siswa nonaktif)</span> :
              (student.schedule || []).map((s, i) => (
                <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-sky-50 text-sky-700 font-semibold">{s.day} · {s.time}</span>
              ))}
            </div>
            {student.notes && <p className="text-sm text-slate-500 mt-3 bg-amber-50 rounded-xl p-3">📝 {student.notes}</p>}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button data-testid="detail-wa-btn" onClick={() => window.open(waLink(student.wa, `Assalamu'alaikum Bapak/Ibu wali dari ${student.name} 🌟`), "_blank")}
              className="rounded-xl bg-emerald-500 hover:bg-emerald-600"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
            {student.portal_token && (
              <div className="flex items-center gap-1 text-xs bg-sky-50 border border-sky-100 rounded-xl px-2 py-1.5 max-w-xs" data-testid="portal-link-box">
                <Link2 className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                <a href={`/portal/${student.portal_token}`} target="_blank" rel="noreferrer" data-testid="portal-link" className="text-sky-700 truncate underline">{`${window.location.origin}/portal/${student.portal_token}`}</a>
                <button data-testid="copy-portal-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/portal/${student.portal_token}`); toast.success("Tautan portal disalin"); }} className="shrink-0 text-sky-600 hover:text-sky-800"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-amber-50">
          {info.map((it, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0"><it.icon className="w-4 h-4 text-orange-500" /></div>
              <div><div className="text-xs text-slate-400">{it.label}</div><div className="text-sm font-medium text-slate-800">{it.value}</div></div>
            </div>
          ))}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-orange-500" /></div>
            <div><div className="text-xs text-slate-400">Orang Tua</div><div className="text-sm font-medium text-slate-800">{student.father_name || "-"} &amp; {student.mother_name || "-"}</div></div>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="journal">
        <TabsList className="rounded-xl bg-amber-50">
          <TabsTrigger value="journal" data-testid="tab-journal" className="rounded-lg">Jurnal Belajar</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance" className="rounded-lg">Kehadiran</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment" className="rounded-lg">Pembayaran</TabsTrigger>
        </TabsList>

        <TabsContent value="journal" className="mt-4 space-y-3">
          {journals.length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">Belum ada jurnal.</p> :
            journals.map((j) => (
              <Card key={j.id} className="p-4 rounded-xl border-amber-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-orange-600">{dateLabel(j.date)}</span>
                  <Stars n={j.rating} />
                </div>
                <div className="font-semibold text-slate-800 mt-1">{j.material}</div>
                {j.homework && <div className="text-sm text-slate-500 mt-1">PR: {j.homework}</div>}
                {j.teacher_note && <div className="text-sm text-slate-500 mt-1 italic">"{j.teacher_note}"</div>}
              </Card>
            ))}
        </TabsContent>

        <TabsContent value="attendance" className="mt-4 space-y-2">
          {attendance.length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">Belum ada catatan kehadiran.</p> :
            attendance.map((a) => (
              <Card key={a.id} className="p-3 rounded-xl border-amber-100 flex items-center justify-between">
                <span className="text-sm text-slate-700">{dateLabel(a.date)}</span>
                <Badge variant="outline" className={`rounded-full ${ATTEND_STATUS[a.status]?.cls}`}>{ATTEND_STATUS[a.status]?.label}</Badge>
              </Card>
            ))}
        </TabsContent>

        <TabsContent value="payment" className="mt-4 space-y-2">
          {payments.length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">Belum ada data pembayaran.</p> :
            payments.map((p) => (
              <Card key={p.id} className="p-3 rounded-xl border-amber-100 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">{monthLabel(p.month)}{p.over_paid && <Badge className="rounded-full border-0 bg-rose-500 text-white text-[10px]">Melebihi tagihan</Badge>}</div>
                  <div className="text-xs text-slate-400">{rupiah(p.amount_paid)} / {rupiah(p.amount_due)}</div>
                  {p.history?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {p.history.map((h) => <span key={h.id} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{dateLabel(h.paid_date)} · {rupiah(h.amount)}{h.note ? ` · ${h.note}` : ""}</span>)}
                    </div>
                  )}
                </div>
                <Badge variant="outline" className={`rounded-full ${PAYMENT_STATUS[p.status]?.cls}`}>{PAYMENT_STATUS[p.status]?.label}</Badge>
              </Card>
            ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
